import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, Package, Edit, Trash2, DollarSign, Check, X,
  Save, Tag, Calendar, CreditCard, Gift, AlertCircle,
  ChevronDown, ChevronUp, Settings, Eye, EyeOff, Receipt,
  MoreVertical, Play, Pause, RefreshCw, XCircle
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Stripe will be loaded dynamically when needed
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const getBackendURL = () => {
        if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
        const origin = window.location.origin;
        if (origin.includes('localhost:3000')) return 'http://localhost:8001';
        return origin + '/pandvah-backend';
      };
      const backendUrl = getBackendURL();
      const response = await axios.get(`${backendUrl}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

const SubscriptionPlans = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'subscriptions' ? 'subscriptions' : 'manage');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  
  // User subscriptions state
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [newPlanId, setNewPlanId] = useState('');
  const [newPricingTierId, setNewPricingTierId] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const itemsPerPage = 10;
  
  // Payment History state
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Actions dropdown state
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const actionMenuRef = useRef(null);
  
  // Plan type selection
  const [showPlanTypeModal, setShowPlanTypeModal] = useState(false);
  const [planType, setPlanType] = useState('single'); // 'single' or 'comparison'
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Form state
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [pricingTiers, setPricingTiers] = useState([
    { tier_id: Date.now().toString(), period: 'monthly', price: 0, currency: 'usd', display_price: '$0/month' }
  ]);
  const [sections, setSections] = useState([
    { 
      section_id: Date.now().toString(), 
      title: '', 
      line_items: [{ 
        item_id: Date.now().toString(), 
        name: '', 
        availability: 'available', 
        additional_info: '' 
      }] 
    }
  ]);
  const [badge, setBadge] = useState('none');
  const [subscriptionButtonEnabled, setSubscriptionButtonEnabled] = useState(true);
  const [freeTrialEnabled, setFreeTrialEnabled] = useState(false);
  const [freeTrialDays, setFreeTrialDays] = useState(14);
  const [buttonText, setButtonText] = useState('Get Started');
  const [isEnabled, setIsEnabled] = useState(false);
  
  // Comparison plan state
  const [comparisonColumns, setComparisonColumns] = useState([]);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Get backend URL
  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    const origin = window.location.origin;
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8001';
    }
    return origin + '/pandvah-backend';
  };
  
  const backendUrl = getBackendURL();
  
  // Fetch plans on mount and when switching to manage tab
  useEffect(() => {
    if (activeTab === 'manage') {
      fetchPlans();
    } else if (activeTab === 'subscriptions') {
      fetchUserSubscriptions();
      fetchPlans(); // Also fetch plans for the change plan feature
    }
  }, [activeTab]);
  
  // Load Stripe
  useEffect(() => {
    getStripePromise();
  }, []);
  
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };
  
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPlans(response.data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      showNotification('Failed to fetch subscription plans', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch user subscriptions with pagination
  const fetchUserSubscriptions = async (page = 1) => {
    try {
      setLoadingSubscriptions(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/user-subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: itemsPerPage }
      });
      
      if (response.data.success) {
        setUserSubscriptions(response.data.subscriptions);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.total_pages);
        setTotalSubscriptions(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      showNotification('Failed to fetch subscriptions', 'error');
    } finally {
      setLoadingSubscriptions(false);
    }
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchUserSubscriptions(newPage);
    }
  };
  
  // Fetch payment history for a subscription
  const fetchPaymentHistory = async (subscriptionId) => {
    try {
      setLoadingPayments(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${backendUrl}/api/subscription-payments/${subscriptionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setPaymentHistory(response.data.payments);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      showNotification('Failed to fetch payment history', 'error');
    } finally {
      setLoadingPayments(false);
    }
  };
  
  // Cancel subscription
  const handleCancelSubscription = async () => {
    if (!selectedSubscription) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/user-subscriptions/${selectedSubscription.id}/cancel`,
        { cancel_immediately: cancelImmediately },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        fetchUserSubscriptions();
        setShowCancelModal(false);
        setSelectedSubscription(null);
        setCancelImmediately(false);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      showNotification(error.response?.data?.detail || 'Failed to cancel subscription', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Pause subscription
  const handlePauseSubscription = async (subscriptionId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/user-subscriptions/${subscriptionId}/pause`,
        { pause_collection: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        fetchUserSubscriptions();
      }
    } catch (error) {
      console.error('Error pausing subscription:', error);
      showNotification(error.response?.data?.detail || 'Failed to pause subscription', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Resume subscription
  const handleResumeSubscription = async (subscriptionId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/user-subscriptions/${subscriptionId}/resume`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        fetchUserSubscriptions();
      }
    } catch (error) {
      console.error('Error resuming subscription:', error);
      showNotification(error.response?.data?.detail || 'Failed to resume subscription', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Change subscription (upgrade/downgrade)
  const handleChangeSubscription = async () => {
    if (!selectedSubscription || !newPlanId || !newPricingTierId) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/user-subscriptions/${selectedSubscription.id}/change`,
        { 
          new_plan_id: newPlanId, 
          new_pricing_tier_id: newPricingTierId,
          proration_behavior: 'create_prorations'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification(response.data.message, 'success');
        fetchUserSubscriptions();
        setShowChangeModal(false);
        setSelectedSubscription(null);
        setNewPlanId('');
        setNewPricingTierId('');
      }
    } catch (error) {
      console.error('Error changing subscription:', error);
      showNotification(error.response?.data?.detail || 'Failed to change subscription', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'canceled': return 'bg-red-100 text-red-800';
      case 'past_due': return 'bg-orange-100 text-orange-800';
      case 'trialing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const handleAddPricingTier = () => {
    setPricingTiers([...pricingTiers, {
      tier_id: Date.now().toString(),
      period: 'monthly',
      price: 0,
      currency: 'usd',
      display_price: '$0/month'
    }]);
  };
  
  const handleRemovePricingTier = (index) => {
    if (pricingTiers.length > 1) {
      setPricingTiers(pricingTiers.filter((_, i) => i !== index));
    }
  };
  
  const handlePricingTierChange = (index, field, value) => {
    const newTiers = [...pricingTiers];
    newTiers[index][field] = value;
    
    // Auto-generate display_price
    if (field === 'price' || field === 'period') {
      const price = field === 'price' ? value : newTiers[index].price;
      const period = field === 'period' ? value : newTiers[index].period;
      newTiers[index].display_price = `$${price}/${period === 'monthly' ? 'month' : 'year'}`;
    }
    
    setPricingTiers(newTiers);
  };
  
  const handleAddSection = () => {
    setSections([...sections, {
      section_id: Date.now().toString(),
      title: '',
      line_items: [{
        item_id: Date.now().toString(),
        name: '',
        availability: 'available',
        additional_info: ''
      }]
    }]);
  };
  
  const handleRemoveSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };
  
  const handleSectionTitleChange = (index, value) => {
    const newSections = [...sections];
    newSections[index].title = value;
    setSections(newSections);
  };
  
  const handleAddLineItem = (sectionIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].line_items.push({
      item_id: Date.now().toString(),
      name: '',
      availability: 'available',
      additional_info: ''
    });
    setSections(newSections);
  };
  
  const handleRemoveLineItem = (sectionIndex, itemIndex) => {
    const newSections = [...sections];
    if (newSections[sectionIndex].line_items.length > 1) {
      newSections[sectionIndex].line_items = newSections[sectionIndex].line_items.filter((_, i) => i !== itemIndex);
      setSections(newSections);
    }
  };
  
  const handleLineItemChange = (sectionIndex, itemIndex, field, value) => {
    const newSections = [...sections];
    newSections[sectionIndex].line_items[itemIndex][field] = value;
    setSections(newSections);
  };
  
  // Comparison plan handlers
  const handleAddComparisonColumn = () => {
    const newColumn = {
      column_id: Date.now().toString(),
      plan_name: '',
      pricing: '',
      yearly_pricing_type: 'discount', // 'discount' or 'fixed'
      yearly_discount: '0',
      yearly_pricing: '',
      membership_fee: '',
      badge: 'none',
      button_text: 'Subscribe',
      subscription_enabled: true,
      feature_availability: {},
      feature_additional_text: {}
    };
    
    // Initialize availability for all existing features
    sections.forEach(section => {
      section.line_items.forEach(item => {
        newColumn.feature_availability[item.item_id] = 'not_available';
        newColumn.feature_additional_text[item.item_id] = '';
      });
    });
    
    setComparisonColumns([...comparisonColumns, newColumn]);
  };
  
  const handleRemoveComparisonColumn = (index) => {
    if (comparisonColumns.length > 1) {
      setComparisonColumns(comparisonColumns.filter((_, i) => i !== index));
    }
  };
  
  const handleComparisonColumnChange = (index, field, value) => {
    const newColumns = [...comparisonColumns];
    newColumns[index][field] = value;
    setComparisonColumns(newColumns);
  };
  
  const handleFeatureAvailabilityChange = (columnIndex, itemId, availability) => {
    const newColumns = [...comparisonColumns];
    newColumns[columnIndex].feature_availability[itemId] = availability;
    setComparisonColumns(newColumns);
  };
  
  const handleFeatureAdditionalTextChange = (columnIndex, itemId, text) => {
    const newColumns = [...comparisonColumns];
    if (!newColumns[columnIndex].feature_additional_text) {
      newColumns[columnIndex].feature_additional_text = {};
    }
    newColumns[columnIndex].feature_additional_text[itemId] = text;
    setComparisonColumns(newColumns);
  };
  
  const handlePlanTypeSelection = (type) => {
    setPlanType(type);
    setShowPlanTypeModal(false);
    setActiveTab('create'); // Switch to create tab after selection
    
    if (type === 'comparison' && comparisonColumns.length === 0) {
      // Initialize with 2 columns
      const col1 = {
        column_id: Date.now().toString(),
        plan_name: '',
        pricing: '',
        yearly_pricing_type: 'discount',
        yearly_discount: '0',
        yearly_pricing: '',
        membership_fee: '',
        badge: 'none',
        button_text: 'Subscribe',
        subscription_enabled: true,
        feature_availability: {},
        feature_additional_text: {}
      };
      const col2 = {
        column_id: (Date.now() + 1).toString(),
        plan_name: '',
        pricing: '',
        yearly_pricing_type: 'discount',
        yearly_discount: '0',
        yearly_pricing: '',
        membership_fee: '',
        badge: 'none',
        button_text: 'Subscribe',
        subscription_enabled: true,
        feature_availability: {},
        feature_additional_text: {}
      };
      setComparisonColumns([col1, col2]);
    }
  };
  
  const resetForm = () => {
    setTitle('');
    setTagline('');
    setPlanType('single');
    setPricingTiers([
      { tier_id: Date.now().toString(), period: 'monthly', price: 0, currency: 'usd', display_price: '$0/month' }
    ]);
    setSections([
      { 
        section_id: Date.now().toString(), 
        title: '', 
        line_items: [{ 
          item_id: Date.now().toString(), 
          name: '', 
          availability: 'available', 
          additional_info: '' 
        }] 
      }
    ]);
    setBadge('none');
    setSubscriptionButtonEnabled(true);
    setFreeTrialEnabled(false);
    setFreeTrialDays(14);
    setButtonText('Get Started');
    setIsEnabled(false);
    setComparisonColumns([]);
    setEditingPlan(null);
    setActiveTab('manage'); // Return to manage tab after reset
  };
  
  const handleCreateOrUpdatePlan = async () => {
    try {
      // Common validation
      if (!title.trim()) {
        showNotification('Please enter a plan title', 'error');
        return;
      }
      if (!tagline.trim()) {
        showNotification('Please enter a tagline', 'error');
        return;
      }
      if (sections.some(s => s.line_items.some(item => !item.name || !item.name.trim()))) {
        showNotification('Please fill in all feature names or remove empty ones', 'error');
        return;
      }
      
      // Type-specific validation
      if (planType === 'single') {
        if (pricingTiers.some(t => !t.price || t.price <= 0)) {
          showNotification('Please enter valid pricing for all tiers', 'error');
          return;
        }
      } else if (planType === 'comparison') {
        if (comparisonColumns.length === 0) {
          showNotification('Please add at least one plan column', 'error');
          return;
        }
        if (comparisonColumns.some(col => !col.plan_name.trim() || !col.pricing.trim())) {
          showNotification('Please fill in plan name and pricing for all columns', 'error');
          return;
        }
      }
      
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build plan data based on type
      const planData = {
        plan_type: planType,
        title,
        tagline,
        sections,
        is_enabled: isEnabled
      };
      
      if (planType === 'single') {
        planData.pricing_tiers = pricingTiers;
        planData.badge = badge;
        planData.subscription_button_enabled = subscriptionButtonEnabled;
        planData.free_trial_enabled = freeTrialEnabled;
        planData.free_trial_days = freeTrialEnabled ? freeTrialDays : null;
        planData.button_text = buttonText;
      } else if (planType === 'comparison') {
        planData.comparison_columns = comparisonColumns;
      }
      
      let response;
      if (editingPlan) {
        // Update existing plan
        response = await axios.put(
          `${backendUrl}/api/subscription-plans/${editingPlan.id}`,
          planData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new plan
        response = await axios.post(
          `${backendUrl}/api/subscription-plans`,
          planData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      if (response.data.success) {
        showNotification(
          editingPlan ? 'Plan updated successfully!' : 'Plan created successfully!',
          'success'
        );
        resetForm();
        setActiveTab('manage');
        fetchPlans();
      }
    } catch (error) {
      console.error('Error saving plan:', error);
      showNotification(error.response?.data?.detail || 'Failed to save plan', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setTitle(plan.title);
    setTagline(plan.tagline);
    setPlanType(plan.plan_type || 'single');
    
    // Convert old string format to new object format for line_items
    const convertedSections = plan.sections.map(section => ({
      ...section,
      line_items: section.line_items.map(item => {
        // If item is already an object with the new structure, use it
        if (typeof item === 'object' && item.name) {
          return item;
        }
        // If item is a string (old format), convert to new format
        return {
          item_id: Date.now().toString() + Math.random(),
          name: item,
          availability: 'available',
          additional_info: ''
        };
      })
    }));
    
    setSections(convertedSections);
    
    // Set type-specific fields
    if (plan.plan_type === 'comparison') {
      setComparisonColumns(plan.comparison_columns || []);
    } else {
      setPricingTiers(plan.pricing_tiers || []);
      setBadge(plan.badge || 'none');
      setSubscriptionButtonEnabled(plan.subscription_button_enabled || true);
      setFreeTrialEnabled(plan.free_trial_enabled || false);
      setFreeTrialDays(plan.free_trial_days || 14);
      setButtonText(plan.button_text || 'Get Started');
    }
    
    setIsEnabled(plan.is_enabled);
    setActiveTab('create');
  };
  
  const handleToggleEnable = async (planId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${backendUrl}/api/subscription-plans/${planId}/toggle`,
        { is_enabled: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification(
          !currentStatus ? 'Plan enabled successfully!' : 'Plan disabled successfully!',
          'success'
        );
        fetchPlans();
      }
    } catch (error) {
      console.error('Error toggling plan:', error);
      showNotification('Failed to toggle plan status', 'error');
    }
  };
  
  const confirmDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteModal(true);
  };
  
  const handleDeletePlan = async () => {
    if (!planToDelete) return;
    
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${backendUrl}/api/subscription-plans/${planToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification('Plan deleted successfully!', 'success');
        setShowDeleteModal(false);
        setPlanToDelete(null);
        fetchPlans();
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      showNotification('Failed to delete plan', 'error');
    } finally {
      setDeleting(false);
    }
  };
  
  const getBadgeDisplay = (badgeType) => {
    const badges = {
      'best_value': { label: 'Best Value', color: 'bg-theme-blue' },
      'most_popular': { label: 'Most Popular', color: 'bg-green-500' },
      'recommended': { label: 'Recommended', color: 'bg-purple-500' },
      'new': { label: 'New', color: 'bg-orange-500' },
      'none': null
    };
    return badges[badgeType];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {notification.message}
        </div>
      )}
      
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-lg font-semibold text-gray-900">Subscription Plans</h1>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => {
                if (editingPlan) {
                  // If editing, just switch to create tab
                  setActiveTab('create');
                } else {
                  // Show plan type selection modal
                  setShowPlanTypeModal(true);
                }
              }}
              className={`py-3 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'create'
                  ? 'border-theme-blue text-theme-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Plus className="h-4 w-4 inline mr-2" />
              {editingPlan ? 'Edit Plan' : 'Create Plan'}
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`py-3 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'manage'
                  ? 'border-theme-blue text-theme-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Package className="h-4 w-4 inline mr-2" />
              Manage Plans
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`py-3 px-6 text-sm font-medium border-b-2 ${
                activeTab === 'subscriptions'
                  ? 'border-theme-blue text-theme-blue'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CreditCard className="h-4 w-4 inline mr-2" />
              Manage Subscriptions
            </button>
          </nav>
        </div>
      </div>
      
      {/* Plan Type Selection Modal */}
      {showPlanTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Plan Type</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Single Plan Option */}
              <button
                onClick={() => handlePlanTypeSelection('single')}
                className="p-6 border-2 border-gray-300 rounded-lg hover:border-theme-blue hover:bg-blue-50 transition-all text-left"
              >
                <Package className="h-8 w-8 text-theme-blue mb-3" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Single Plan</h4>
                <p className="text-sm text-gray-600">
                  Create an individual subscription plan with pricing tiers, features, and customization options.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Multiple pricing tiers
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Feature sections
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Badges and customization
                  </li>
                </ul>
              </button>
              
              {/* Comparison Plan Option */}
              <button
                onClick={() => handlePlanTypeSelection('comparison')}
                className="p-6 border-2 border-gray-300 rounded-lg hover:border-theme-blue hover:bg-blue-50 transition-all text-left"
              >
                <Settings className="h-8 w-8 text-purple-600 mb-3" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Comparison Plan</h4>
                <p className="text-sm text-gray-600">
                  Create a comparison table with multiple plans side-by-side, perfect for showing plan differences.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-gray-600">
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Multiple plan columns
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Feature comparison matrix
                  </li>
                  <li className="flex items-center">
                    <Check className="h-4 w-4 text-green-600 mr-2" />
                    Visual checkmarks
                  </li>
                </ul>
              </button>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowPlanTypeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Tab Content */}
      {activeTab === 'create' ? (
        // Create/Edit Plan Tab
        <div className="bg-white rounded-xl shadow-sm p-6">
          {editingPlan && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm text-blue-800">
                <AlertCircle className="h-4 w-4 inline mr-2" />
                Editing: {editingPlan.title}
              </span>
              <button
                onClick={resetForm}
                className="text-sm text-theme-blue hover:text-blue-700"
              >
                Cancel Edit
              </button>
            </div>
          )}
          
          {/* Conditional Form based on Plan Type */}
          {planType === 'single' ? (
            // SINGLE PLAN FORM
            <>
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Plan Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Professional, Enterprise, Home Business"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline *
                </label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="e.g., Perfect for getting started online"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Pricing Tiers */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Pricing Tiers</h3>
              <button
                onClick={handleAddPricingTier}
                className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Tier
              </button>
            </div>
            
            {pricingTiers.map((tier, index) => (
              <div key={tier.tier_id} className="mb-3 p-4 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Tier {index + 1}</span>
                  {pricingTiers.length > 1 && (
                    <button
                      onClick={() => handleRemovePricingTier(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Period</label>
                    <select
                      value={tier.period}
                      onChange={(e) => handlePricingTierChange(index, 'period', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={tier.price}
                      onChange={(e) => handlePricingTierChange(index, 'price', parseFloat(e.target.value) || 0)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                </div>
                
                <div className="mt-2">
                  <span className="text-xs text-gray-500">Display: {tier.display_price}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Feature Sections */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Feature Sections</h3>
              <button
                onClick={handleAddSection}
                className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Section
              </button>
            </div>
            
            {sections.map((section, sectionIndex) => (
              <div key={section.section_id} className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Section {sectionIndex + 1}</span>
                  {sections.length > 1 && (
                    <button
                      onClick={() => handleRemoveSection(sectionIndex)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">
                    Section Header (Optional)
                  </label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => handleSectionTitleChange(sectionIndex, e.target.value)}
                    placeholder="e.g., Core Features, Premium Add-ons"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-xs text-gray-600">Line Items</label>
                    <button
                      onClick={() => handleAddLineItem(sectionIndex)}
                      className="text-xs text-theme-blue hover:text-blue-700 flex items-center"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </button>
                  </div>
                  
                  {/* Column Headers */}
                  <div className="grid grid-cols-12 gap-2 mb-2 px-1">
                    <div className="col-span-6 text-xs font-medium text-gray-600">Feature Name</div>
                    <div className="col-span-2 text-xs font-medium text-gray-600">Status</div>
                    <div className="col-span-3 text-xs font-medium text-gray-600">Additional Info</div>
                  </div>
                  
                  {section.line_items.map((item, itemIndex) => (
                    <div key={item.item_id || itemIndex} className="grid grid-cols-12 gap-2 mb-3 items-start">
                      {/* Feature Name */}
                      <div className="col-span-6">
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleLineItemChange(sectionIndex, itemIndex, 'name', e.target.value)}
                          placeholder="e.g., Professional Single Page Site"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      {/* Availability Status */}
                      <div className="col-span-2">
                        <select
                          value={item.availability || 'available'}
                          onChange={(e) => handleLineItemChange(sectionIndex, itemIndex, 'availability', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="available">✓ Available</option>
                          <option value="not_available">✗ Not Available</option>
                        </select>
                      </div>
                      
                      {/* Additional Info (price/percentage/limit) */}
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={item.additional_info || ''}
                          onChange={(e) => handleLineItemChange(sectionIndex, itemIndex, 'additional_info', e.target.value)}
                          placeholder="e.g., $5/mo, 100GB, 50%"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      {/* Remove Button */}
                      <div className="col-span-1 flex justify-center">
                        {section.line_items.length > 1 && (
                          <button
                            onClick={() => handleRemoveLineItem(sectionIndex, itemIndex)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Additional Settings */}
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Additional Settings</h3>
            
            <div className="space-y-4">
              {/* Badge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="h-4 w-4 inline mr-2" />
                  Badge
                </label>
                <select
                  value={badge}
                  onChange={(e) => setBadge(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="none">No Badge</option>
                  <option value="best_value">Best Value</option>
                  <option value="most_popular">Most Popular</option>
                  <option value="recommended">Recommended</option>
                  <option value="new">New</option>
                </select>
              </div>
              
              {/* Button Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Button Text
                </label>
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                  placeholder="e.g., Get Started, Subscribe Now"
                  className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              
              {/* Toggles */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={subscriptionButtonEnabled}
                    onChange={(e) => setSubscriptionButtonEnabled(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <CreditCard className="h-4 w-4 inline mr-1" />
                    Enable Subscription Button (with payment form)
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={freeTrialEnabled}
                    onChange={(e) => setFreeTrialEnabled(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Gift className="h-4 w-4 inline mr-1" />
                    Enable Free Trial
                  </span>
                </label>
                
                {freeTrialEnabled && (
                  <div className="ml-6">
                    <label className="block text-xs text-gray-600 mb-1">Trial Days</label>
                    <input
                      type="number"
                      min="1"
                      value={freeTrialDays}
                      onChange={(e) => setFreeTrialDays(parseInt(e.target.value) || 14)}
                      className="w-32 p-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                )}
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <Eye className="h-4 w-4 inline mr-1" />
                    Enable plan (visible to customers)
                  </span>
                </label>
              </div>
            </div>
          </div>
          </>
          ) : (
            // COMPARISON PLAN FORM
            <>
          {/* Basic Information */}
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-4">Comparison Table Information</h3>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Table Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Puppies, Business Plans, Membership Tiers"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder="e.g., Choose the best plan for your puppy"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Feature Sections (Shared across all plans) */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Shared Features List</h3>
              <button
                onClick={handleAddSection}
                className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Section
              </button>
            </div>
            
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              These features will appear in all plan columns. You'll select availability for each plan below.
            </div>
            
            {sections.map((section, sectionIndex) => (
              <div key={section.section_id} className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex justify-between items-start mb-3">
                  <span className="text-sm font-medium text-gray-700">Section {sectionIndex + 1}</span>
                  {sections.length > 1 && (
                    <button
                      onClick={() => handleRemoveSection(sectionIndex)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                <div className="mb-3">
                  <label className="block text-xs text-gray-600 mb-1">
                    Section Header (Optional)
                  </label>
                  <input
                    type="text"
                    value={section.title}
                    onChange={(e) => handleSectionTitleChange(sectionIndex, e.target.value)}
                    placeholder="e.g., Core Services, Premium Features"
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  />
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-xs text-gray-600">Feature Names</label>
                    <button
                      onClick={() => handleAddLineItem(sectionIndex)}
                      className="text-xs text-theme-blue hover:text-blue-700 flex items-center"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Feature
                    </button>
                  </div>
                  
                  {section.line_items.map((item, itemIndex) => (
                    <div key={item.item_id || itemIndex} className="flex items-center mb-2">
                      <input
                        type="text"
                        value={item.name || ''}
                        onChange={(e) => handleLineItemChange(sectionIndex, itemIndex, 'name', e.target.value)}
                        placeholder="e.g., Unlimited Office Visits, Vaccines"
                        className="flex-1 p-2 border border-gray-300 rounded text-sm"
                      />
                      {section.line_items.length > 1 && (
                        <button
                          onClick={() => handleRemoveLineItem(sectionIndex, itemIndex)}
                          className="ml-2 text-red-600 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Plan Columns */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">Plan Columns</h3>
              <button
                onClick={handleAddComparisonColumn}
                className="text-sm text-theme-blue hover:text-blue-700 flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Plan
              </button>
            </div>
            
            {comparisonColumns.length === 0 ? (
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500">
                <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No plans added yet. Click "Add Plan" to create your first column.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {comparisonColumns.map((column, colIndex) => (
                  <div key={column.column_id} className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                    {/* Column Header */}
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-base font-semibold text-gray-900">Plan {colIndex + 1}</h4>
                      {comparisonColumns.length > 1 && (
                        <button
                          onClick={() => handleRemoveComparisonColumn(colIndex)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                    
                    {/* Plan Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Plan Name *</label>
                        <input
                          type="text"
                          value={column.plan_name}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'plan_name', e.target.value)}
                          placeholder="e.g., Primary, Plus, Premium"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Monthly Pricing *</label>
                        <input
                          type="number"
                          value={column.pricing}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'pricing', e.target.value)}
                          placeholder="e.g., 57.95"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Yearly Pricing Type</label>
                        <select
                          value={column.yearly_pricing_type || 'discount'}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'yearly_pricing_type', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="discount">Discount Percentage</option>
                          <option value="fixed">Fixed Yearly Price</option>
                        </select>
                      </div>
                      
                      {(column.yearly_pricing_type || 'discount') === 'discount' ? (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Yearly Discount (%)</label>
                          <select
                            value={column.yearly_discount || '0'}
                            onChange={(e) => handleComparisonColumnChange(colIndex, 'yearly_discount', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          >
                            {[0, 5, 10, 15, 20, 25].map(pct => (
                              <option key={pct} value={pct}>{pct}% discount</option>
                            ))}
                          </select>
                          {column.pricing && column.yearly_discount > 0 && (
                            <p className="text-xs text-green-600 mt-1">
                              Yearly: ${((parseFloat(column.pricing) * 12) * (1 - column.yearly_discount / 100)).toFixed(2)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Fixed Yearly Price</label>
                          <input
                            type="number"
                            value={column.yearly_pricing || ''}
                            onChange={(e) => handleComparisonColumnChange(colIndex, 'yearly_pricing', e.target.value)}
                            placeholder="e.g., 500"
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                          />
                          {column.pricing && column.yearly_pricing && (
                            <p className="text-xs text-green-600 mt-1">
                              Savings: ${((parseFloat(column.pricing) * 12) - parseFloat(column.yearly_pricing)).toFixed(2)}/yr
                            </p>
                          )}
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Membership Fee (Optional)</label>
                        <input
                          type="text"
                          value={column.membership_fee || ''}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'membership_fee', e.target.value)}
                          placeholder="e.g., $80"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Badge</label>
                        <select
                          value={column.badge}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'badge', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        >
                          <option value="none">No Badge</option>
                          <option value="best_value">Best Value</option>
                          <option value="most_popular">Most Popular</option>
                          <option value="recommended">Recommended</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Button Text</label>
                        <input
                          type="text"
                          value={column.button_text}
                          onChange={(e) => handleComparisonColumnChange(colIndex, 'button_text', e.target.value)}
                          placeholder="e.g., Subscribe, Get Started"
                          className="w-full p-2 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      
                      <div className="flex items-center">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={column.subscription_enabled}
                            onChange={(e) => handleComparisonColumnChange(colIndex, 'subscription_enabled', e.target.checked)}
                            className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-xs text-gray-700">Enable Subscription</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Feature Availability for this column */}
                    <div className="mt-4 pt-4 border-t border-blue-300">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Feature Availability</h5>
                      
                      {sections.map((section, sectionIdx) => (
                        <div key={section.section_id} className="mb-3">
                          {section.title && (
                            <div className="text-xs font-medium text-gray-600 mb-2 uppercase">
                              {section.title}
                            </div>
                          )}
                          
                          {section.line_items.map((item, itemIdx) => (
                            <div key={item.item_id} className="mb-2 p-3 bg-white rounded border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-700 flex-1">{item.name || 'Unnamed Feature'}</span>
                                <select
                                  value={column.feature_availability[item.item_id] || 'not_available'}
                                  onChange={(e) => handleFeatureAvailabilityChange(colIndex, item.item_id, e.target.value)}
                                  className="ml-3 p-1 border border-gray-300 rounded text-sm w-36"
                                >
                                  <option value="available">✓ Available</option>
                                  <option value="not_available">✗ Not Available</option>
                                  <option value="empty">— Empty (No Icon)</option>
                                </select>
                              </div>
                              <div className="flex items-center">
                                <label className="text-xs text-gray-600 mr-2 w-20">Custom Text:</label>
                                <input
                                  type="text"
                                  value={column.feature_additional_text?.[item.item_id] || ''}
                                  onChange={(e) => handleFeatureAdditionalTextChange(colIndex, item.item_id, e.target.value)}
                                  placeholder="e.g., 3 times, 5GB, Unlimited"
                                  className="flex-1 p-1 border border-gray-300 rounded text-sm"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Enable Plan */}
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={(e) => setIsEnabled(e.target.checked)}
                className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                <Eye className="h-4 w-4 inline mr-1" />
                Enable comparison table (visible to customers)
              </span>
            </label>
          </div>
          </>
          )}
          
          {/* Action Buttons (shared by both types) */}
          <div className="flex justify-end gap-3">
            <button
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {editingPlan ? 'Cancel' : 'Reset'}
            </button>
            <button
              onClick={handleCreateOrUpdatePlan}
              disabled={loading}
              style={{ backgroundColor: loading ? 'rgba(41, 173, 211, 0.6)' : 'var(--theme-primary)' }}
              className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-all disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingPlan ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </>
              )}
            </button>
          </div>
        </div>
      ) : activeTab === 'manage' ? (
        // Manage Plans Tab
        <div className="bg-white rounded-xl shadow-sm">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
            </div>
          ) : plans.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No subscription plans found</p>
              <button
                onClick={() => setActiveTab('create')}
                className="mt-4 px-4 py-2 text-theme-blue hover:text-blue-700"
              >
                Create your first plan
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => {
                  const badgeInfo = getBadgeDisplay(plan.badge);
                  const isComparison = plan.plan_type === 'comparison';
                  
                  return (
                    <div key={plan.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      {/* Badge */}
                      {badgeInfo && !isComparison && (
                        <div className={`${badgeInfo.color} text-white text-xs font-medium py-1 px-3 text-center`}>
                          {badgeInfo.label}
                        </div>
                      )}
                      
                      {/* Plan Content */}
                      <div className="p-6">
                        {/* Header */}
                        <div className="mb-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{plan.title}</h3>
                              {isComparison && (
                                <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded mt-1 inline-block">
                                  Comparison Table
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {plan.is_enabled ? (
                                <Eye className="h-4 w-4 text-green-600" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600">{plan.tagline}</p>
                        </div>
                        
                        {/* Pricing - Only for Single Plans */}
                        {!isComparison && plan.pricing_tiers && (
                          <div className="mb-4">
                            {plan.pricing_tiers.map((tier, idx) => (
                            <div key={tier.tier_id} className="flex items-baseline mb-1">
                              <span className="text-2xl font-bold text-theme-blue">
                                ${tier.price}
                              </span>
                              <span className="text-sm text-gray-600 ml-1">
                                /{tier.period === 'monthly' ? 'month' : 'year'}
                              </span>
                            </div>
                          ))}
                          {plan.free_trial_enabled && (
                            <span className="text-xs text-green-600">
                              <Gift className="h-3 w-3 inline" /> {plan.free_trial_days} days free trial
                            </span>
                          )}
                        </div>
                        )}
                        
                        {/* Features Preview */}
                        <div className="mb-4 space-y-2">
                          {plan.sections.slice(0, 1).map((section) => (
                            <div key={section.section_id}>
                              {section.line_items.slice(0, 3).map((item, idx) => {
                                // Handle both old string format and new object format
                                const itemName = typeof item === 'string' ? item : item.name;
                                const isAvailable = typeof item === 'string' ? true : item.availability === 'available';
                                const additionalInfo = typeof item === 'string' ? null : item.additional_info;
                                
                                return (
                                  <div key={idx} className="flex items-start text-sm">
                                    {isAvailable ? (
                                      <Check className="h-4 w-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                                    ) : (
                                      <X className="h-4 w-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1">
                                      <span className={isAvailable ? 'text-gray-700' : 'text-gray-400 line-through'}>
                                        {itemName}
                                      </span>
                                      {additionalInfo && (
                                        <span className="ml-2 text-xs font-medium text-theme-blue bg-blue-50 px-2 py-0.5 rounded">
                                          {additionalInfo}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                          {plan.sections[0]?.line_items.length > 3 && (
                            <p className="text-xs text-gray-500 ml-6">
                              +{plan.sections[0].line_items.length - 3} more features
                            </p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="pt-4 border-t border-gray-200 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Status</span>
                            <button
                              onClick={() => handleToggleEnable(plan.id, plan.is_enabled)}
                              className={`px-3 py-1 rounded text-xs font-medium ${
                                plan.is_enabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {plan.is_enabled ? 'Enabled' : 'Disabled'}
                            </button>
                          </div>
                          
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditPlan(plan)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-center"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                            <button
                              onClick={() => confirmDeletePlan(plan)}
                              className="flex-1 px-3 py-2 border border-red-300 rounded text-sm text-red-700 hover:bg-red-50 flex items-center justify-center"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Manage Subscriptions Tab
        <div className="bg-white rounded-xl shadow-sm">
          {loadingSubscriptions ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
            </div>
          ) : userSubscriptions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No active subscriptions found</p>
              <p className="text-sm text-gray-400 mt-2">Subscriptions will appear here when customers subscribe to your plans</p>
            </div>
          ) : (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period End</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {userSubscriptions.map((subscription) => (
                      <tr key={subscription.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{subscription.user_name || 'N/A'}</div>
                          <div className="text-sm text-gray-500">{subscription.user_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{subscription.plan_title}</div>
                          <div className="text-xs text-gray-500 capitalize">{subscription.billing_period}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            ${subscription.price}/{subscription.billing_period === 'monthly' ? 'mo' : 'yr'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(subscription.status)}`}>
                            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                          </span>
                          {subscription.cancel_at_period_end && (
                            <span className="ml-2 px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-800">
                              Canceling
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {subscription.current_period_end ? 
                            new Date(subscription.current_period_end).toLocaleDateString() : 
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="relative" ref={openActionMenuId === subscription.id ? actionMenuRef : null}>
                            <button
                              onClick={() => setOpenActionMenuId(openActionMenuId === subscription.id ? null : subscription.id)}
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              disabled={loading}
                            >
                              <MoreVertical className="h-5 w-5 text-gray-500" />
                            </button>
                            
                            {openActionMenuId === subscription.id && (
                              <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                                {/* Payment History - Always visible */}
                                <button
                                  onClick={() => {
                                    setSelectedSubscription(subscription);
                                    fetchPaymentHistory(subscription.id);
                                    setShowPaymentHistoryModal(true);
                                    setOpenActionMenuId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Receipt className="h-4 w-4 text-purple-500" />
                                  Payment History
                                </button>
                                
                                {subscription.status !== 'canceled' && (
                                  <>
                                    {/* Pause/Resume */}
                                    {subscription.status === 'paused' ? (
                                      <button
                                        onClick={() => {
                                          handleResumeSubscription(subscription.id);
                                          setOpenActionMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <Play className="h-4 w-4 text-green-500" />
                                        Resume Subscription
                                      </button>
                                    ) : subscription.status === 'active' && (
                                      <button
                                        onClick={() => {
                                          handlePauseSubscription(subscription.id);
                                          setOpenActionMenuId(null);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <Pause className="h-4 w-4 text-yellow-500" />
                                        Pause Subscription
                                      </button>
                                    )}
                                    
                                    {/* Change Plan */}
                                    <button
                                      onClick={() => {
                                        setSelectedSubscription(subscription);
                                        setShowChangeModal(true);
                                        setOpenActionMenuId(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <RefreshCw className="h-4 w-4 text-theme-blue" />
                                      Change Plan
                                    </button>
                                    
                                    {/* Divider */}
                                    <div className="border-t border-gray-100 my-1"></div>
                                    
                                    {/* Cancel */}
                                    <button
                                      onClick={() => {
                                        setSelectedSubscription(subscription);
                                        setShowCancelModal(true);
                                        setOpenActionMenuId(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <XCircle className="h-4 w-4" />
                                      Cancel Subscription
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalSubscriptions)} of {totalSubscriptions} subscriptions
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loadingSubscriptions}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loadingSubscriptions}
                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-[#29add6] text-white'
                                : 'border border-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loadingSubscriptions}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Payment History Modal */}
      {showPaymentHistoryModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full mx-4 border border-slate-700 shadow-2xl">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Payment History
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {selectedSubscription.plan_title} - {selectedSubscription.user_name || selectedSubscription.user_email}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowPaymentHistoryModal(false);
                  setSelectedSubscription(null);
                  setPaymentHistory([]);
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-5 max-h-96 overflow-y-auto">
              {loadingPayments ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#29add6]"></div>
                  <span className="ml-2 text-slate-400">Loading payments...</span>
                </div>
              ) : paymentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Receipt className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No payment records found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paymentHistory.map((payment) => (
                    <div 
                      key={payment.id} 
                      className="bg-slate-700/50 rounded-lg p-4 border border-slate-600"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-medium">{payment.description || payment.plan_title}</p>
                          <p className="text-sm text-slate-400">
                            {payment.created_at ? new Date(payment.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-white">
                            ${parseFloat(payment.amount || 0).toFixed(2)}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            payment.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            payment.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            payment.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {payment.status ? payment.status.charAt(0).toUpperCase() + payment.status.slice(1) : 'Unknown'}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs text-slate-400">
                        <span>Type: {payment.payment_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Payment'}</span>
                        <span>Method: {payment.payment_method?.charAt(0).toUpperCase() + payment.payment_method?.slice(1) || 'Card'}</span>
                        <span>Period: {payment.billing_period?.charAt(0).toUpperCase() + payment.billing_period?.slice(1) || 'Monthly'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowPaymentHistoryModal(false);
                  setSelectedSubscription(null);
                  setPaymentHistory([]);
                }}
                className="w-full px-4 py-3 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-base font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Cancel Subscription Modal */}
      {showCancelModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl max-w-md w-full mx-4 border border-slate-700 shadow-2xl">
            <div className="p-5 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">Cancel Subscription</h3>
              <p className="text-base text-slate-300 mt-1">
                Cancel subscription for <strong className="text-white">{selectedSubscription.user_name || selectedSubscription.user_email}</strong>?
              </p>
            </div>
            
            <div className="p-5 space-y-3">
              <label className="flex items-start gap-3 p-3 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
                <input
                  type="radio"
                  name="cancelType"
                  checked={!cancelImmediately}
                  onChange={() => setCancelImmediately(false)}
                  className="mt-1 accent-[#29add6]"
                />
                <div>
                  <div className="font-medium text-white">Cancel at end of period</div>
                  <div className="text-sm text-slate-400">
                    Subscription will remain active until {selectedSubscription.current_period_end ? 
                      new Date(selectedSubscription.current_period_end).toLocaleDateString() : 
                      'the end of the billing period'
                    }
                  </div>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 transition-colors">
                <input
                  type="radio"
                  name="cancelType"
                  checked={cancelImmediately}
                  onChange={() => setCancelImmediately(true)}
                  className="mt-1 accent-[#29add6]"
                />
                <div>
                  <div className="font-medium text-white">Cancel immediately</div>
                  <div className="text-sm text-slate-400">
                    Subscription will be canceled right away. No refund will be issued.
                  </div>
                </div>
              </label>
            </div>
            
            <div className="flex gap-3 p-5 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedSubscription(null);
                  setCancelImmediately(false);
                }}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors text-base font-medium"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-slate-600 disabled:text-slate-400 transition-colors text-base font-medium"
              >
                {loading ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Change Subscription Modal */}
      {showChangeModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl max-w-lg w-full mx-4 border border-slate-700 shadow-2xl">
            <div className="p-5 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white">Change Subscription Plan</h3>
              <p className="text-base text-slate-300 mt-1">
                Current plan: <strong className="text-white">{selectedSubscription.plan_title}</strong> (${selectedSubscription.price}/{selectedSubscription.billing_period === 'monthly' ? 'mo' : 'yr'})
              </p>
            </div>
            
            <div className="p-5 space-y-4">
              {(() => {
                // Find the current plan to check if it's a comparison plan
                const currentPlan = plans.find(p => p.id === selectedSubscription.plan_id);
                const isComparisonPlan = currentPlan?.plan_type === 'comparison';
                
                if (isComparisonPlan && currentPlan?.comparison_columns) {
                  // Show other columns from the same comparison plan
                  const otherColumns = currentPlan.comparison_columns.filter(
                    col => col.column_id !== selectedSubscription.pricing_tier_id
                  );
                  
                  const formatBadge = (badge) => {
                    const badges = {
                      'best_value': 'Best Value',
                      'most_popular': 'Most Popular',
                      'recommended': 'Recommended',
                      'new': 'New'
                    };
                    return badges[badge] || '';
                  };
                  
                  return (
                    <div>
                      <label className="block text-base font-medium text-slate-200 mb-3">Select New Option</label>
                      <div className="space-y-2">
                        {otherColumns.map((col) => {
                          const price = parseFloat(col.pricing || col.price || 0);
                          const badgeLabel = formatBadge(col.badge);
                          const isSelected = newPricingTierId === col.column_id;
                          
                          return (
                            <button
                              key={col.column_id}
                              onClick={() => {
                                setNewPlanId(currentPlan.id);
                                setNewPricingTierId(col.column_id);
                              }}
                              className={`w-full p-4 rounded-lg border text-left transition-all ${
                                isSelected 
                                  ? 'border-[#29add6] bg-[#29add6]/10' 
                                  : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/50'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <div>
                                  <span className="text-white font-medium">{col.plan_name}</span>
                                  {badgeLabel && (
                                    <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                      col.badge === 'most_popular' ? 'bg-[#29add6] text-white' :
                                      col.badge === 'best_value' ? 'bg-green-500 text-white' :
                                      col.badge === 'recommended' ? 'bg-purple-500 text-white' :
                                      'bg-orange-500 text-white'
                                    }`}>
                                      {badgeLabel}
                                    </span>
                                  )}
                                </div>
                                <span className="text-white font-semibold">${price.toFixed(2)}/mo</span>
                              </div>
                              {(() => {
                                const priceDiff = price - selectedSubscription.price;
                                if (priceDiff > 0) {
                                  return <p className="text-xs text-emerald-400 mt-1">Upgrade (+${priceDiff.toFixed(2)}/mo)</p>;
                                } else if (priceDiff < 0) {
                                  return <p className="text-xs text-amber-400 mt-1">Downgrade (-${Math.abs(priceDiff).toFixed(2)}/mo)</p>;
                                }
                                return null;
                              })()}
                            </button>
                          );
                        })}
                      </div>
                      {otherColumns.length === 0 && (
                        <p className="text-slate-400 text-sm">No other options available in this plan.</p>
                      )}
                    </div>
                  );
                } else {
                  // For single plans, show pricing tiers
                  const currentPlanTiers = currentPlan?.pricing_tiers?.filter(
                    t => t.tier_id !== selectedSubscription.pricing_tier_id
                  ) || [];
                  
                  return (
                    <div>
                      <label className="block text-base font-medium text-slate-200 mb-3">Select New Pricing</label>
                      <div className="space-y-2">
                        {currentPlanTiers.map((tier) => {
                          const isSelected = newPricingTierId === tier.tier_id;
                          
                          return (
                            <button
                              key={tier.tier_id}
                              onClick={() => {
                                setNewPlanId(currentPlan.id);
                                setNewPricingTierId(tier.tier_id);
                              }}
                              className={`w-full p-4 rounded-lg border text-left transition-all ${
                                isSelected 
                                  ? 'border-[#29add6] bg-[#29add6]/10' 
                                  : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/50'
                              }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-white font-medium">
                                  {tier.period === 'monthly' ? 'Monthly' : 'Yearly'} Plan
                                </span>
                                <span className="text-white font-semibold">
                                  ${tier.price}/{tier.period === 'monthly' ? 'mo' : 'yr'}
                                </span>
                              </div>
                              {(() => {
                                const priceDiff = tier.price - selectedSubscription.price;
                                if (priceDiff > 0) {
                                  return <p className="text-xs text-emerald-400 mt-1">Upgrade (+${priceDiff.toFixed(2)})</p>;
                                } else if (priceDiff < 0) {
                                  return <p className="text-xs text-amber-400 mt-1">Downgrade (-${Math.abs(priceDiff).toFixed(2)})</p>;
                                }
                                return null;
                              })()}
                            </button>
                          );
                        })}
                      </div>
                      {currentPlanTiers.length === 0 && (
                        <p className="text-slate-400 text-sm">No other pricing options available.</p>
                      )}
                    </div>
                  );
                }
              })()}
            </div>
            
            <div className="flex gap-3 p-5 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowChangeModal(false);
                  setSelectedSubscription(null);
                  setNewPlanId('');
                  setNewPricingTierId('');
                }}
                disabled={loading}
                className="flex-1 px-4 py-3 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 transition-colors text-base font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeSubscription}
                disabled={loading || !newPlanId || !newPricingTierId}
                className="flex-1 px-4 py-3 bg-[#29add6] text-white rounded-lg hover:bg-[#1e9bc4] disabled:bg-slate-600 disabled:text-slate-400 transition-colors text-base font-medium"
              >
                {loading ? 'Changing...' : 'Change Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Subscription Plan</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{planToDelete?.title}"? This action cannot be undone.
              {planToDelete?.stripe_product_id && (
                <span className="block mt-2 text-red-600">
                  Note: The associated Stripe product will be archived.
                </span>
              )}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setPlanToDelete(null);
                }}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePlan}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
